import { Component, Inject, Optional } from '@angular/core';
import { FileUploader, FileUploadModule } from 'ng2-file-upload';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Configuration } from '../../../core/av-api';
import { OAuthService } from 'angular-oauth2-oidc';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BASE_PATH } from '../../../core/gb-api';
import { AppCustomConfigService } from '../../../core/services/app-custom-config.service';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { Observable } from 'rxjs';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { sanatizedSaveAs } from '../../../shared/functions/file.functions';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { RubrikComponent } from '../../../shared/components/rubrik/rubrik.component';
import { DecimalPipe, NgClass, NgStyle } from '@angular/common';

// const URL = '/api/';
const env = (window as any).environment;
const BASE_URL = env.apiUrl + '/base/config/document';

@Component({
  selector: 'cap-file-manager',
  templateUrl: './config-file-manager.component.html',
  styleUrls: ['./config-file-manager.component.scss'],
  imports: [ContentHeaderComponent, TitleComponent, RubrikComponent, TranslatePipe, FileUploadModule, NgClass, NgStyle, DecimalPipe]
})
export class ConfigFileManagerComponent {
  headers;
  public configuration = new Configuration();

  public hasBaseDropZoneOver = false;
  public uploader: FileUploader;

  constructor(
    protected httpClient: HttpClient,
    private readonly translate: TranslateService,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() configuration: Configuration,
    private readonly oauthService: OAuthService,
    private readonly capAlertService: CapAlertService,
    private readonly appCustomConfigService: AppCustomConfigService
  ) {
    keysToTranslate<string>([
      'configuration.konfigurationVerwalten',
      'configuration.konfigurationHeraufladen',
      'configuration.konfigurationHerunterladen',
      'configuration.fileAuswaehlen',
      'configuration.dropZone',
      'configuration.download',
      'configuration.upload',
      'configuration.uploadQueue',
      'configuration.queueLength',
      'configuration.size',
      'configuration.progress',
      'configuration.status',
      'configuration.actions',
      'configuration.upload',
      'configuration.cancel',
      'configuration.remove',
      'configuration.name',
      'ungueltigeXmlDateiHochgeladen'
    ]);
    this.headers = this.initHeaders(new HttpHeaders());
    this.uploader = new FileUploader({
      url: BASE_URL,
      disableMultipart: false, // 'DisableMultipart' must be 'true' for formatDataFunction to be called.
      headers: this.headers.lazyUpdate,
      formatDataFunctionIsAsync: false
    });

    this.uploader.onSuccessItem = (item, response, status, headers) => {
      this.appCustomConfigService.reloadLinkConfigs();
      this.capAlertService.success('Config ' + this.translate.instant('wurdeErfolgreichGespeichert') + '.');
    };

    this.uploader.onCompleteItem = (item, response, status, headers) => {
      this.onSuccessItem(item, response, status, headers);
    };

    this.uploader.onAfterAddingFile = (fileItem: any) => {
      this.onAfterAddingFile(fileItem);
    };

    this.uploader.onErrorItem = (item, response, status, headers) => {
      this.onErrorItem(item, response, status, headers);
    };
  }

  static save(data: any[], filename: string, propertyBag: FilePropertyBag) {
    let file;
    try {
      file = new File(data, filename, propertyBag);
    } catch (e) {
      // IE 11 does not know File API
      file = new Blob(data, propertyBag);
      file.name = filename;
      file.lastModifiedDate = new Date();
      file.lastModified += file.lastModifiedDate;
    }
    sanatizedSaveAs(file);
  }

  private initHeaders(defaultHeaders) {
    let bearerHeaders = defaultHeaders;
    bearerHeaders = bearerHeaders.set('Authorization', 'Bearer ' + this.oauthService.getAccessToken());
    const httpHeaderAccepts: string[] = ['application/json'];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      bearerHeaders = bearerHeaders.set('Accept', httpHeaderAcceptSelected);
    }
    return bearerHeaders;
  }

  public downloadConfiguration() {
    this.getConfiguration().subscribe((data) => {
      ConfigFileManagerComponent.save([data], 'config.xml', { type: 'text/xml' });
    });
  }

  private getConfiguration(): Observable<Blob> {
    const headers = this.headers;
    return this.httpClient.get(`${BASE_URL}`, {
      headers: headers,
      responseType: 'blob',
      withCredentials: this.configuration.withCredentials
    });
  }

  private onSuccessItem(item, response, status, headers) {
    if (status === 200) {
      setTimeout(() => {
        this.close();
      }, 1000);
    }
  }

  private onAfterAddingFile(fileItem) {
    if (this.uploader.queue.length > 1) {
      this.uploader.removeFromQueue(this.uploader.queue[0]);
    }
  }

  private onErrorItem(item, response, status, headers) {
    if (status === 500) {
      this.capAlertService.danger(this.translate.instant('ungueltigeXmlDateiHochgeladen'));
    }
  }

  close() {
    this.uploader.clearQueue();
  }

  public fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }
}
